import { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { focusAreaWithName } from '../types/focus_area'
import {
  makeStyles,
  tokens,
  Badge,
  Button,
  Caption1,
  Dropdown,
  MessageBar,
  MessageBarBody,
  Option,
  SearchBox,
  Spinner,
  Subtitle1,
  Subtitle2,
  Switch,
  Title2,
  type DropdownProps,
} from '@fluentui/react-components'
import { activatable, type ActivateEvent } from '../lib/activatable'
import { applicationsOpen, applyBy } from '../lib/applicationsOpen'
import { useDemands } from '../hooks/useDemands'
import { useCreateApplication, useMyApplications } from '../hooks/useApplications'
import { useMyInvitations } from '../hooks/useInvitations'
import { useMySkills } from '../hooks/useUserSkills'
import { useCapabilities } from '../hooks/useCapabilities'
import { coveredSkills, skillIndex } from '../lib/skillMatch'
import { FOCUS_AREAS } from '../types/focus_area'
import type { Demand } from '../types/demand'
import { useToast } from '../components/ToastProvider'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { ApplyDialog } from '../components/ApplyDialog'
import { apiErrorMessage } from '../lib/apiError'
import { OpportunitySummary } from '../components/OpportunitySummary'
import { OpportunityDescription } from '../components/OpportunityDescription'
import { PITCH_LABELS, requestorNames } from '../lib/labels'
import { Target16Regular } from '@fluentui/react-icons'
import { useOpportunityEngagement, useOpportunityReminders, useSaveOpportunity } from '../hooks/useOpportunityEngagement'
import { opportunityReminders } from '../lib/opportunityReminders'
import { TitleWithHelp } from '../components/TitleWithHelp'

/**
 * Talent — Browse opportunities (mockup §5301-5494).
 *
 * Mirrors the mockup's `renderTalentBrowse`, `renderBrowseOpp`, `renderBrowseGrid`
 * on the v2 substrate. Uses the canonical `useDemands({ stage: 'open_to_application' })`
 * hook and `useCreateApplication` for the apply dialog.
 */

type SortKey = 'closing' | 'newest'
type StageFilter = 'all' | 'open' | 'staffed'

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '1280px',
  },
  pageHead: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  caption: {
    color: tokens.colorNeutralForeground3,
  },
  captionInvited: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  searchBox: {
    flex: '1 1 240px',
    minWidth: '220px',
  },
  filterDropdown: {
    minWidth: '180px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
    cursor: 'pointer',
  },
  cardInvited: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    boxShadow: tokens.shadow8,
  },
  invitedFlag: {
    position: 'absolute',
    top: '-8px',
    right: tokens.spacingHorizontalM,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: tokens.borderRadiusCircular,
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  cardTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  matchLine: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  impact: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  impactIcon: {
    flexShrink: 0,
    marginTop: '2px',
    color: tokens.colorBrandForeground1,
  },
  impactText: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  skillsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  cardFoot: {
    marginTop: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalXXL,
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
    color: tokens.colorNeutralForeground3,
  },
  stageFilterRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalM,
  },
  fitWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fitLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  fitBarTrack: {
    width: '100%',
    height: '6px',
    background: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  fitBarFill: {
    height: '100%',
    background: tokens.colorBrandBackground,
    transition: 'width 200ms ease',
  },
  cardActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
})

interface EnrichedOpp extends Demand {
  matchSkills: number
  totalSkills: number
  matchedNames: string[]
  isInvited: boolean
  daysLeft: number | null
}

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null
  const close = new Date(iso).getTime()
  if (Number.isNaN(close)) return null
  const now = Date.now()
  const diffMs = close - now
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
}

const sortFns: Record<SortKey, (a: EnrichedOpp, b: EnrichedOpp) => number> = {
  closing: (a, b) => (a.daysLeft ?? Infinity) - (b.daysLeft ?? Infinity),
  newest: (a, b) => b.created_at.localeCompare(a.created_at),
}

export function Opportunities() {
  const styles = useStyles()
  const { showToast } = useToast()

  // Effective, not real: under impersonation this page must read as the
  // impersonated person — including whether the opt-in banner shows.
  const user = useEffectiveUser()
  const [search, setSearch] = useState('')
  // Default the FA filter to the talent's focus area when they chose exactly
  // one. It used to take the FIRST of several, which hid the rest: a talent
  // who kept all four saw FA1 alone. Talents can clear or change it;
  // council/admin land on the unfiltered view. (#781 — reads
  // preferred_focus_areas, not the admin-managed focus_area_code.)
  const [faFilter, setFaFilter] = useState<string>(() => {
    const prefs =
      user?.role === 'talent' || user?.role === 'member' ? (user?.preferred_focus_areas ?? []) : []
    return prefs.length === 1 ? (prefs[0] ?? '') : ''
  })
  const [sortBy, setSortBy] = useState<SortKey>('closing')
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [savedOnly, setSavedOnly] = useState(false)
  const engagement = useOpportunityEngagement()
  const saveOpportunity = useSaveOpportunity()
  const remindersMutation = useOpportunityReminders()
  const savedIds = useMemo(() => engagement.data?.saved_demand_ids ?? [], [engagement.data])

  // Fetch ALL active demands (no stage filter) — we filter client-side
  // to show open_to_application + any demand the talent is invited to.
  const demandsQuery = useDemands({ page_size: 200, allPages: true })
  const unavailableSavedIds = savedIds.filter(
    (id) => !(demandsQuery.data?.items ?? []).some((d) => d.id === id),
  )
  const skillsQuery = useMySkills()
  const myAppsQuery = useMyApplications()
  const createApplication = useCreateApplication()

  const [applyingTo, setApplyingTo] = useState<Demand | null>(null)

  const mySkillNames = useMemo(
    () => (skillsQuery.data ?? []).map((s) => s.capability_name),
    [skillsQuery.data],
  )
  // Which category each skill sits in, so a whole category on either side
  // counts (lib/skillMatch). Without it, terms match by name alone.
  const capabilitiesQuery = useCapabilities()
  const skillIdx = useMemo(() => skillIndex(capabilitiesQuery.data), [capabilitiesQuery.data])
  // `isSuccess` alone was the old test, and an empty list is a success: every
  // card then claimed "Skill fit — 0 of 5" against a profile that has listed
  // no skills at all. 224 of 235 people are in exactly that state, and the bar
  // reads as a verdict on them rather than as a gap in what we know.
  const skillsKnown = skillsQuery.isSuccess && mySkillNames.length > 0
  const skillsMissing = skillsQuery.isSuccess && mySkillNames.length === 0

  // Demands the user already applied to (self-initiated)
  const appliedDemandIds = useMemo(
    () => new Set(
      (myAppsQuery.data?.items ?? [])
        .filter((a) => a.status === 'pending' || a.status === 'approved')
        .map((a) => a.demand_id)
    ),
    [myAppsQuery.data],
  )

  // Demands the user was invited to (from real Invitation records)
  const myInvitationsQuery = useMyInvitations()
  const invitedDemandIds = useMemo(() => {
    const invs = myInvitationsQuery.data ?? []
    return new Set(
      invs
        .filter((inv) => inv.status === 'pending')
        .map((inv) => inv.demand_id),
    )
  }, [myInvitationsQuery.data])

  const enriched: EnrichedOpp[] = useMemo(() => {
    const items = demandsQuery.data?.items ?? []
    const activeStages = ['resourcing']
    // Show demands that are resourcing AND still recruiting (not yet staffed),
    // OR ones you're invited to that are still in an active stage.
    const visible = items.filter(
      (d) =>
        (savedOnly && savedIds.includes(d.id)) ||
        (d.stage === 'resourcing' && d.staffed_status !== 'on') ||
        (invitedDemandIds.has(d.id) && activeStages.includes(d.stage)),
    )
    return visible.map((d) => {
      const matched = coveredSkills(skillIdx, mySkillNames, d.skills ?? [])
      return {
        ...d,
        matchSkills: matched.length,
        totalSkills: (d.skills ?? []).length,
        matchedNames: matched,
        isInvited: invitedDemandIds.has(d.id),
        daysLeft: daysUntil(d.application_closes_at),
      }
    })
  }, [demandsQuery.data, mySkillNames, skillIdx, invitedDemandIds, savedOnly, savedIds])

  const filtered = useMemo(() => {
    let opps = enriched
    if (savedOnly) opps = opps.filter((o) => savedIds.includes(o.id))
    if (search) {
      const q = search.toLowerCase()
      opps = opps.filter(
        (o) =>
          o.title.toLowerCase().includes(q) ||
          (o.description ?? '').toLowerCase().includes(q) ||
          (o.pitch_project ?? '').toLowerCase().includes(q) ||
          (o.pitch_impact ?? '').toLowerCase().includes(q) ||
          o.skills.some((s) => s.toLowerCase().includes(q)),
      )
    }
    if (faFilter) {
      // An invitation is addressed to you; a browsing filter shouldn't hide it.
      opps = opps.filter((o) => o.focus_area_code === faFilter || o.isInvited)
    }
    if (stageFilter !== 'all') {
      // open+staffed are one `resourcing` stage now, split by the staffed flag.
      opps = opps.filter((o) =>
        stageFilter === 'open'
          ? o.stage === 'resourcing' && o.staffed_status !== 'on'
          : o.stage === 'resourcing' && o.staffed_status === 'on',
      )
    }
    const fn = sortFns[sortBy]
    const sorted = [...opps].sort((a, b) => {
      if (a.isInvited && !b.isInvited) return -1
      if (!a.isInvited && b.isInvited) return 1
      return fn(a, b)
    })
    return sorted
  }, [enriched, search, faFilter, sortBy, stageFilter, savedOnly, savedIds])

  const reminders = opportunityReminders(
    demandsQuery.data?.items ?? [], savedIds,
    mySkillNames,
    new Set([
      ...(myAppsQuery.data?.items ?? []).map((a) => a.demand_id),
      ...(myInvitationsQuery.data ?? []).map((i) => i.demand_id),
    ]),
    Date.now(),
    skillIdx,
  )

  const invitedCount = filtered.filter((o) => o.isInvited).length

  if (demandsQuery.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading opportunities..." />
      </div>
    )
  }

  if (demandsQuery.isError) {
    return (
      <div className={styles.page}>
        <MessageBar intent="error">
          <MessageBarBody>
            Failed to load opportunities. {apiErrorMessage(demandsQuery.error)}
          </MessageBarBody>
        </MessageBar>
      </div>
    )
  }

  const total = filtered.length

  return (
    <div className={styles.page}>
      {/* Browsing is open to everyone; APPLYING needs the activated talent
          profile (basic-user split, UAT 2026-09-11). A plain member landing
          here by deep link gets the door explained up front rather than a
          refusal at the last click — the backend enforces it either way. */}
      {user?.role === 'member' && !user?.talent_profile_active && (
        <MessageBar intent="info" data-testid="opportunities-optin-banner">
          <MessageBarBody>
            To apply, activate your talent profile in{' '}
            <RouterLink to="/profile">My profile</RouterLink>.
          </MessageBarBody>
        </MessageBar>
      )}
      <header className={styles.pageHead}>
        <TitleWithHelp topic="opportunities">
          <Title2 as="h1">Browse opportunities</Title2>
        </TitleWithHelp>
        <Caption1 className={styles.caption}>
          {total} {savedOnly ? 'saved ' : 'open '}{total === 1 ? 'opportunity' : 'opportunities'}
          {invitedCount > 0 && (
            <>
              {' · '}
              <span className={styles.captionInvited}>{invitedCount} invited for you</span>
              {' (pinned to top)'}
            </>
          )}
        </Caption1>
      </header>

      {engagement.isError && (
        <MessageBar intent="error"><MessageBarBody>
          Could not load saved opportunities or reminder preferences.{' '}
          <Button onClick={() => void engagement.refetch()}>Try again</Button>
        </MessageBarBody></MessageBar>
      )}
      <div className={styles.toolbar}>
        <Button
          aria-pressed={savedOnly}
          disabled={!engagement.data}
          onClick={() => {
            setSavedOnly(!savedOnly)
            setFaFilter('')
            setSearch('')
            setStageFilter('all')
          }}
        >
          {savedOnly ? 'Show all opportunities' : `Saved (${savedIds.length})`}
        </Button>
        <Switch
          label="Show closing-soon reminders here"
          checked={engagement.data?.reminders_enabled ?? false}
          disabled={!engagement.data || remindersMutation.isPending}
          onChange={(_, data) => remindersMutation.mutate(data.checked, {
            onError: (error) => showToast({
              title: 'Could not update reminders', body: apiErrorMessage(error), intent: 'error',
            }),
          })}
        />
      </div>
      {engagement.data?.reminders_enabled && (
        <section aria-label="Closing-soon reminders">
          {skillsQuery.isError || myAppsQuery.isError || myInvitationsQuery.isError ? (
            <MessageBar intent="warning"><MessageBarBody>
              Could not check all reminder information. Refresh to try again.
            </MessageBarBody></MessageBar>
          ) : skillsQuery.isPending || myAppsQuery.isPending || myInvitationsQuery.isPending ? (
            <Spinner size="tiny" label="Checking reminders..." />
          ) : (
            <>
              <Subtitle2>Deadline within 2 days</Subtitle2>
              {reminders.length ? reminders.map((d) => (
                <p key={d.id}><RouterLink to={`/demand/${d.id}`}>{d.title}</RouterLink>
                  {' - apply by '}{new Date(d.application_closes_at!).toLocaleString()}
                </p>
              )) : <p>No relevant opportunities have a deadline within 2 days.</p>}
              <RouterLink to="/profile">Manage your skills</RouterLink>
            </>
          )}
        </section>
      )}
      {savedOnly && (
        <div>
          {unavailableSavedIds.map((id) => (
            <p key={id}>
              Saved opportunity #{id} is not available in the current view.{' '}
              <Button size="small" disabled={saveOpportunity.isPending}
                onClick={() => saveOpportunity.mutate({ id, saved: false }, {
                  onError: (error) => showToast({
                    title: 'Could not remove saved opportunity', body: apiErrorMessage(error), intent: 'error',
                  }),
                })}
              >Remove saved opportunity #{id}</Button>
            </p>
          ))}
        </div>
      )}
      <div className={styles.toolbar} role="search">
        <SearchBox
          className={styles.searchBox}
          placeholder="Search by title, description, or skill..."
          value={search}
          onChange={(_, data) => setSearch(data.value)}
          aria-label="Search opportunities"
        />
        <FaFilter value={faFilter} onChange={setFaFilter} className={styles.filterDropdown} />
        <SortSelect value={sortBy} onChange={setSortBy} className={styles.filterDropdown} />
        {(search || faFilter || stageFilter !== 'all') && (
          <Button
            appearance="subtle"
            size="small"
            onClick={() => {
              setSearch('')
              setFaFilter('')
              setStageFilter('all')
            }}
          >
            Clear
          </Button>
        )}
      </div>

      <div className={styles.stageFilterRow} role="tablist" aria-label="Filter by stage">
        {([
          { value: 'all', label: 'All stages' },
          { value: 'open', label: 'Accepting applications' },
          { value: 'staffed', label: 'Staffed' },
        ] as { value: StageFilter; label: string }[]).map((opt) => (
          <Button
            key={opt.value}
            size="small"
            appearance={stageFilter === opt.value ? 'primary' : 'subtle'}
            onClick={() => setStageFilter(opt.value)}
            aria-pressed={stageFilter === opt.value}
            role="tab"
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {skillsMissing && filtered.length > 0 && (
        <MessageBar intent="info" data-testid="opp-no-skills">
          <MessageBarBody>
            We don’t know your skills yet.{' '}
            <RouterLink to="/profile">Add them</RouterLink> and each card will show its fit.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Said once, here. Each card used to repeat it under a "0 of N" bar
          that already says the same thing. */}
      {skillsKnown && filtered.length > 0 && filtered.every((o) => o.matchSkills === 0) && (
        <MessageBar intent="info" data-testid="opp-no-overlap">
          <MessageBarBody>
            None of these list your skills yet. You can still apply.
          </MessageBarBody>
        </MessageBar>
      )}

      {filtered.length === 0 ? (
        <div className={styles.empty} role="status">
          <Subtitle1>No open opportunities match your filters</Subtitle1>
          <Caption1>
            {savedOnly ? 'Save an opportunity to return to it later, or clear your filters.' : 'Adjust your search or clear filters.'}
          </Caption1>
        </div>
      ) : (
        <div className={styles.grid} data-testid="opp-grid">
          {filtered.map((o) => (
            <OpportunityCard
              key={o.id}
              opp={o}
              alreadyApplied={appliedDemandIds.has(o.id)}
              skillsKnown={skillsKnown}
              onApply={() => setApplyingTo(o)}
              saved={savedIds.includes(o.id)}
              saving={saveOpportunity.isPending || !engagement.data}
              onSave={() => saveOpportunity.mutate({ id: o.id, saved: !savedIds.includes(o.id) }, {
                onError: (error) => showToast({
                  title: 'Could not update saved opportunity', body: apiErrorMessage(error), intent: 'error',
                }),
              })}
            />
          ))}
        </div>
      )}

      {applyingTo && (
        <ApplyDialog
          demandTitle={applyingTo.title}
          demand={applyingTo}
          open={!!applyingTo}
          onClose={() => setApplyingTo(null)}
          onSubmit={async (input) => {
            try {
              await createApplication.mutateAsync({
                demand_id: applyingTo.id,
                ...input,
              })
              showToast({
                title: 'Application submitted',
                body: `Your application for "${applyingTo.title}" was sent.`,
                intent: 'success',
              })
              setApplyingTo(null)
            } catch (err: unknown) {
              showToast({
                title: 'Could not submit',
                body: apiErrorMessage(err, 'Please try again.'),
                intent: 'error',
              })
            }
          }}
          submitting={createApplication.isPending}
        />
      )}
    </div>
  )
}

function FaFilter({
  value,
  onChange,
  className,
}: {
  value: string
  onChange: (v: string) => void
  className?: string
}) {
  const onSelect: DropdownProps['onOptionSelect'] = (_, data) => {
    onChange(data.optionValue ?? '')
  }
  return (
    <Dropdown
      className={className}
      aria-label="Filter by Focus Area"
      placeholder="All Focus Areas"
      value={value ? focusAreaWithName(value) : 'All Focus Areas'}
      selectedOptions={[value]}
      onOptionSelect={onSelect}
    >
      <Option value="" text="All Focus Areas">
        All Focus Areas
      </Option>
      {FOCUS_AREAS.map((fa) => (
        <Option key={fa.code} value={fa.code} text={focusAreaWithName(fa.code)}>
          {fa.code} — {fa.name}
        </Option>
      ))}
    </Dropdown>
  )
}

function SortSelect({
  value,
  onChange,
  className,
}: {
  value: SortKey
  onChange: (v: SortKey) => void
  className?: string
}) {
  const labels: Record<SortKey, string> = {
    closing: 'Sort: Deadline soonest',
    newest: 'Sort: Newest',
  }
  const onSelect: DropdownProps['onOptionSelect'] = (_, data) => {
    if (data.optionValue) onChange(data.optionValue as SortKey)
  }
  return (
    <Dropdown
      className={className}
      aria-label="Sort opportunities"
      value={labels[value]}
      selectedOptions={[value]}
      onOptionSelect={onSelect}
    >
      <Option value="closing" text={labels.closing}>{labels.closing}</Option>
      <Option value="newest" text={labels.newest}>{labels.newest}</Option>
    </Dropdown>
  )
}

function OpportunityCard({
  opp,
  alreadyApplied,
  skillsKnown,
  onApply,
  saved,
  saving,
  onSave,
}: {
  opp: EnrichedOpp
  alreadyApplied: boolean
  skillsKnown: boolean
  onApply: () => void
  saved: boolean
  saving: boolean
  onSave: () => void
}) {
  const styles = useStyles()
  const navigate = useNavigate()
  const demandLabel = opp.demand_id ?? `D-${opp.id}`
  // The server's rule (`sql_posted()`): a passed deadline closes nothing,
  // council or the caretaker closing applications does (2026-10-16).
  const canApply = applicationsOpen(opp)

  const handleCardActivate = (e: ActivateEvent) => {
    // Don't navigate if clicking the Apply button
    if ((e.target as HTMLElement).closest('button')) return
    navigate(`/demand/${opp.id}`)
  }

  return (
    <article
      className={`${styles.card} ${opp.isInvited ? styles.cardInvited : ''}`}
      data-testid={`opp-card-${opp.id}`}
      aria-label={opp.title}
      role="link"
      {...activatable(handleCardActivate)}
    >
      {opp.isInvited && (
        <div className={styles.invitedFlag}>
          <span aria-hidden>{'✨'}</span> Invited
        </div>
      )}
      <div className={styles.cardHead}>
        <Badge appearance="tint" color="brand">
          {focusAreaWithName(opp.focus_area_code)}
        </Badge>
        <Caption1 className={styles.matchLine}>
          {!canApply
            ? 'Applications closed'
            : (applyBy(opp.application_closes_at) ?? (opp.application_closes_at ? 'Still open' : 'No deadline'))}
        </Caption1>
      </div>
      <Subtitle2 as="h3" className={styles.cardTitle}>
        {opp.title}
      </Subtitle2>
      <Caption1>{demandLabel}</Caption1>
      {/* The caretaker's pitch leads when there is one (#1499); the
          submitter's description is the fallback, and stays on the detail page. */}
      <OpportunityDescription text={opp.pitch_project || opp.description} />
      {opp.pitch_impact && (
        <div className={styles.impact} data-testid="opp-impact">
          <Target16Regular aria-hidden className={styles.impactIcon} />
          <span className={styles.impactText}>
            <strong>{PITCH_LABELS.pitch_impact}:</strong> {opp.pitch_impact}
          </span>
        </div>
      )}
      <OpportunitySummary demand={opp} matchedSkills={skillsKnown ? opp.matchedNames : undefined} />
      {opp.totalSkills > 0 && skillsKnown && (
        <div className={styles.fitWrap} aria-label={`Skill fit: ${opp.matchSkills} of ${opp.totalSkills}`}>
          <div className={styles.fitLabel}>
            <span>Skill fit</span>
            <span>{opp.matchSkills} of {opp.totalSkills} skills</span>
          </div>
          <div
            className={styles.fitBarTrack}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={opp.totalSkills}
            aria-valuenow={opp.matchSkills}
          >
            <div
              className={styles.fitBarFill}
              style={{ width: `${Math.round((opp.matchSkills / opp.totalSkills) * 100)}%` }}
            />
          </div>
        </div>
      )}
      <div className={styles.cardFoot}>
        <Caption1>
          {opp.requestor_name
            ? `Requested by ${requestorNames(opp.requestor_name)}`
            : `Demand ${demandLabel}`}
        </Caption1>
        <div className={styles.cardActions}>
          <Button
            size="small"
            aria-label={`${saved ? 'Unsave' : 'Save'} ${opp.title}`}
            aria-pressed={saved}
            disabled={saving}
            onClick={onSave}
          >
            {saved ? 'Saved' : 'Save'}
          </Button>
          <Button
            appearance="subtle"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/demand/${opp.id}`)
            }}
            aria-label={`View details for ${opp.title}`}
          >
            View details
          </Button>
          {alreadyApplied ? (
            <Badge appearance="filled" color="success" size="small">
              Already applied
            </Badge>
          ) : canApply ? (
            <Button
              appearance="primary"
              size="small"
              onClick={onApply}
              aria-label={`Apply to ${opp.title}`}
            >
              Apply
            </Button>
          ) : <Badge>Not accepting applications</Badge>}
        </div>
      </div>
    </article>
  )
}

export default Opportunities
